import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router';
import { 
  ArrowLeft, LayoutDashboard, Lock, Plus, Pencil, Shield, 
  AlertTriangle, CheckCircle2, MoreVertical, LayoutGrid, Eye,
  HelpCircle,
  LayoutList
} from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Badge } from './ui/badge';
import { Switch } from './ui/switch';
import { Dialog, DialogContent } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';
import { DashboardWizard } from './DashboardWizard';
import { formatCategoryLabel } from '../utils/normalizeAcronyms';
import { projectId } from '../utils/supabase/info';
import { HoverCard, HoverCardContent, HoverCardTrigger } from './ui/hover-card';
import { QUEUE_TEMPLATES } from '../constants/queueTemplates';
import { CANONICAL_INTENT_ALIGNMENTS } from '../constants/intentAlignments';
import type { DashboardDefinition, DashboardDomainConfig } from '../types/dashboard';

interface DashboardConfigViewProps {
  companyId: string;
  sowId: string;
  accessToken: string;
  currentUser: any;
}

export function DashboardConfigView({ companyId, sowId, accessToken, currentUser }: DashboardConfigViewProps) {
  const navigate = useNavigate();
  const [config, setConfig] = useState<DashboardDomainConfig>({
    enabled: false,
    selectedPackage: 'basic',
    definitions: [],
    status: 'not_started'
  });
  const [loading, setLoading] = useState(true);
  
  // Wizard State
  const [isWizardOpen, setIsWizardOpen] = useState(false);
  const [editingDashboard, setEditingDashboard] = useState<DashboardDefinition | null>(null);

  // Available work types (Mock for MVP, would fetch real ones)
  const workTypes = [
      { id: 'service-request-fulfillment', name: 'Service Requests' },
      { id: 'incident-management', name: 'Incidents' },
      { id: 'change-management', name: 'Changes' },
      { id: 'problem-management', name: 'Problems' },
  ];

  // Load Config
  useEffect(() => {
    const loadConfig = async () => {
      setLoading(true);
      try {
        const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
            headers: { 'Authorization': `Bearer ${accessToken}` }
        });
        if (res.ok) {
            const data = await res.json();
            const dashConfig = data.engagementConfig?.dashboards || {
                enabled: false,
                selectedPackage: 'basic',
                definitions: [],
                status: 'not_started'
            };
            setConfig(dashConfig);
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    loadConfig();
  }, [companyId, sowId, accessToken]);

  const saveConfig = async (newConfig: DashboardDomainConfig) => {
      try {
          // We need to fetch current engagement config first to merge
          const res = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
              headers: { 'Authorization': `Bearer ${accessToken}` }
          });
          if (!res.ok) throw new Error("Failed to fetch");
          const { engagementConfig } = await res.json();

          // Merge dashboards
          const updatedEngagementConfig = {
              ...engagementConfig,
              dashboards: newConfig
          };

          const saveRes = await fetch(`https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd/engagement-config/${companyId}/${sowId}`, {
              method: 'PUT',
              headers: { 
                  'Authorization': `Bearer ${accessToken}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify(updatedEngagementConfig)
          });

          if (saveRes.ok) {
              setConfig(newConfig);
              return true;
          }
          return false;
      } catch (e) {
          console.error(e);
          toast.error("Failed to save configuration");
          return false;
      }
  };

  const handleToggleEnable = async (enabled: boolean) => {
      const newConfig = { ...config, enabled };
      await saveConfig(newConfig);
  };

  const handlePackageChange = async (pkg: 'basic' | 'advanced' | 'executive') => {
      if (pkg === 'executive') return; // Stubbed
      const newConfig = { ...config, selectedPackage: pkg };
      await saveConfig(newConfig);
  };

  const handleSaveDashboard = async (dashboard: DashboardDefinition) => {
      const newDefinitions = [...config.definitions];
      const existingIdx = newDefinitions.findIndex(d => d.id === dashboard.id);
      
      if (existingIdx >= 0) {
          newDefinitions[existingIdx] = dashboard;
      } else {
          newDefinitions.push(dashboard);
      }

      const newConfig = { ...config, definitions: newDefinitions };
      const success = await saveConfig(newConfig);
      
      if (success) {
          setIsWizardOpen(false);
          setEditingDashboard(null);
          toast.success("Dashboard saved");
      }
  };

  const openWizard = (dashboard?: DashboardDefinition) => {
      setEditingDashboard(dashboard || null);
      setIsWizardOpen(true);
  };

  // Gating Check
  const lockedCount = config.definitions.filter(d => d.locked).length;
  const requiredCount = config.selectedPackage === 'basic' ? 3 : 5; // Arbitrary logic for Advanced
  const isReady = config.enabled && lockedCount >= requiredCount;
  
  // Guidance Helper
  const Guidance = ({ title, text }: { title: string, text: string }) => (
      <HoverCard>
          <HoverCardTrigger asChild>
              <HelpCircle className="w-4 h-4 text-slate-400 cursor-help ml-2 inline-block align-text-bottom" />
          </HoverCardTrigger>
          <HoverCardContent className="w-80 bg-slate-900 text-slate-50 border-slate-800">
              <div className="space-y-1">
                  <h4 className="text-sm font-semibold">{title}</h4>
                  <p className="text-xs opacity-90">{text}</p>
              </div>
          </HoverCardContent>
      </HoverCard>
  );

  if (loading) return <div className="p-8 text-center">Loading...</div>;

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <header className="bg-white border-b px-6 py-4 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Grid
          </Button>
          <div className="h-6 w-px bg-gray-200" />
          <div>
            <h1 className="text-xl font-bold text-gray-900">Dashboards Domain</h1>
            <p className="text-xs text-muted-foreground">Canonical Configuration • v2026.2</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
            {!isReady && config.enabled && (
                <Badge variant="destructive" className="animate-pulse">
                    Domain Blocked: {requiredCount - lockedCount} more locked definitions required
                </Badge>
            )}
            <Badge variant={isReady ? 'default' : 'outline'}>
                {isReady ? 'Ready for Build' : 'Configuration In Progress'}
            </Badge>
        </div>
      </header>

      <main className="flex-1 max-w-6xl w-full mx-auto p-6 space-y-6">
        
        {/* DASH_G_01 & DASH_G_02: Domain Control */}
        <Card className="border-l-4 border-l-indigo-600">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                <div className="space-y-1">
                    <CardTitle className="text-lg flex items-center gap-2">
                        <LayoutDashboard className="h-5 w-5 text-indigo-600" />
                        Domain Policy
                    </CardTitle>
                    <CardDescription>
                        Global configuration for the Dashboards domain.
                    </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    <span className="text-sm font-medium">DASH_G_01: Enable Dashboards</span>
                    <Switch checked={config.enabled} onCheckedChange={handleToggleEnable} />
                    <Guidance title="DASH_G_01: Enable Dashboards" text="Determines whether dashboards are in scope for this engagement. If disabled, no dashboard work will be performed." />
                </div>
            </CardHeader>
            {config.enabled && (
                <CardContent className="border-t pt-6">
                    <div className="mb-4 flex items-center gap-2">
                         <span className="text-sm font-semibold text-slate-700">DASH_G_02: Dashboard Package Level</span>
                         <Guidance title="DASH_G_02: Package Level" text="Package selection determines required dashboard count, complexity, and data sources." />
                    </div>
                    <div className="flex gap-4">
                        <div 
                            className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${config.selectedPackage === 'basic' ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500' : 'bg-white hover:border-gray-300'}`}
                            onClick={() => handlePackageChange('basic')}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-indigo-900">Basic Package</span>
                                {config.selectedPackage === 'basic' && <CheckCircle2 className="h-5 w-5 text-indigo-600" />}
                            </div>
                            <p className="text-sm text-gray-600">Operational dashboards for daily agent and manager use.</p>
                            <div className="mt-3 text-xs font-medium text-indigo-700 bg-indigo-100/50 px-2 py-1 rounded inline-block">
                                Minimum 3 Locked Definitions
                            </div>
                        </div>

                        <div 
                            className={`flex-1 border rounded-lg p-4 cursor-pointer transition-all ${config.selectedPackage === 'advanced' ? 'bg-indigo-50 border-indigo-500 ring-1 ring-indigo-500' : 'bg-white hover:border-gray-300'}`}
                            onClick={() => handlePackageChange('advanced')}
                        >
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-indigo-900">Advanced Package</span>
                                {config.selectedPackage === 'advanced' && <CheckCircle2 className="h-5 w-5 text-indigo-600" />}
                            </div>
                            <p className="text-sm text-gray-600">Cross-team operational intelligence and trend analysis.</p>
                            <div className="mt-3 text-xs font-medium text-indigo-700 bg-indigo-100/50 px-2 py-1 rounded inline-block">
                                Minimum 5 Locked Definitions
                            </div>
                        </div>

                        <div className="flex-1 border rounded-lg p-4 opacity-50 cursor-not-allowed bg-gray-50">
                            <div className="flex justify-between items-start mb-2">
                                <span className="font-semibold text-gray-500">Executive Package</span>
                                <Badge variant="outline" className="text-[10px]">Disabled</Badge>
                            </div>
                            <p className="text-sm text-gray-500">Outcome and value dashboards (Post-MVP).</p>
                        </div>
                    </div>
                </CardContent>
            )}
        </Card>

        {/* Dashboard Library */}
        {config.enabled && (
            <div className="space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900">Dashboard Library</h2>
                        <p className="text-sm text-gray-500">
                            Required: <span className="font-bold">{requiredCount}</span> • 
                            Locked: <span className={`font-bold ${lockedCount >= requiredCount ? 'text-green-600' : 'text-amber-600'}`}>{lockedCount}</span>
                        </p>
                    </div>
                    <Button onClick={() => openWizard()} className="bg-indigo-600 hover:bg-indigo-700">
                        <Plus className="h-4 w-4 mr-2" /> New Definition
                    </Button>
                </div>

                {config.definitions.length === 0 ? (
                    <div className="border-2 border-dashed border-gray-200 rounded-lg p-12 text-center bg-gray-50/50">
                        <LayoutGrid className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900">No Dashboards Defined</h3>
                        <p className="text-gray-500 mb-4 max-w-md mx-auto">Create definitions to specify reporting requirements. You need to lock at least {requiredCount} dashboards to proceed.</p>
                        <Button variant="outline" onClick={() => openWizard()}>Start Definition</Button>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {config.definitions.map(dash => (
                            <Card key={dash.id} className="relative group hover:shadow-md transition-shadow">
                                <CardHeader className="pb-3">
                                    <div className="flex justify-between items-start">
                                        <Badge variant={dash.locked ? 'secondary' : 'outline'} className={dash.locked ? 'bg-indigo-100 text-indigo-700 border-indigo-200' : 'text-slate-500'}>
                                            {dash.locked ? 'Ready for Build' : 'Draft'}
                                        </Badge>
                                        {dash.locked && <Lock className="h-4 w-4 text-indigo-400" />}
                                    </div>
                                    <CardTitle className="mt-2 text-base leading-tight">{dash.name}</CardTitle>
                                    <CardDescription className="line-clamp-2 text-xs mt-1">{dash.description || "No description provided."}</CardDescription>
                                </CardHeader>
                                <CardContent>
                                    <div className="space-y-2 text-xs text-gray-500">
                                        <div className="flex items-center gap-2">
                                            <Shield className="h-3 w-3" />
                                            <span className="capitalize">{dash.intent ? formatCategoryLabel(dash.intent) : 'No Intent'}</span>
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <LayoutDashboard className="h-3 w-3" />
                                            <span>{dash.visualizations.length} Visualizations</span>
                                        </div>
                                        {dash.advanced && Object.keys(dash.advanced).length > 0 && (
                                            <div className="mt-2 pt-2 border-t border-gray-100 text-[10px] text-amber-600 font-medium">
                                                Advanced Config Active
                                            </div>
                                        )}
                                    </div>
                                    <div className="mt-4 pt-4 border-t flex justify-end">
                                        <Button variant="ghost" size="sm" onClick={() => openWizard(dash)} className="h-8 text-xs">
                                            {dash.locked ? <Eye className="h-3 w-3 mr-1.5" /> : <Pencil className="h-3 w-3 mr-1.5" />}
                                            {dash.locked ? 'View Spec' : 'Edit'}
                                        </Button>
                                    </div>
                                </CardContent>
                            </Card>
                        ))}
                    </div>
                )}
            </div>
          )}

        {/* Suggested Queues Panel (Advisory) */}
        {config.enabled && (
            <Card className="border-t-4 border-t-slate-500 bg-slate-50">
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div className="space-y-1">
                            <CardTitle className="text-lg flex items-center gap-2">
                                <LayoutList className="h-5 w-5 text-slate-600" />
                                Suggested Queue Alignment
                            </CardTitle>
                            <CardDescription>
                                Match your dashboards to these standard queues to ensure data visibility.
                            </CardDescription>
                        </div>
                        <Button variant="outline" size="sm" onClick={() => navigate(`../queues`)} className="bg-white hover:bg-slate-50">
                            Manage Queues <ArrowLeft className="ml-2 h-4 w-4 rotate-180" />
                        </Button>
                    </div>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {/* Group alignments by Domain Scope for display */}
                        {['incident', 'change', 'problem', 'cross_domain'].map(domain => {
                            const domainQueues = QUEUE_TEMPLATES.filter(q => q.domain === domain);
                            if (domainQueues.length === 0) return null;

                            const getDomainTitle = (d: string) => {
                                if (d === 'incident') return 'Incident Command';
                                if (d === 'change') return 'Change Governance';
                                if (d === 'problem') return 'Problem Health';
                                return 'Cross-Domain / Operations';
                            };

                            return (
                                <div key={domain} className="space-y-3 p-4 bg-white rounded-lg border shadow-sm">
                                    <h4 className="font-semibold text-sm capitalize flex items-center gap-2 text-slate-800">
                                        {getDomainTitle(domain)}
                                        <Badge variant="secondary" className="text-[10px] h-5 px-1.5 bg-slate-100 text-slate-600">
                                            {domainQueues.length}
                                        </Badge>
                                    </h4>
                                    
                                    <div className="text-[10px] text-slate-400 mb-2 italic">
                                        Common dashboards for this intent:
                                    </div>

                                    <ul className="space-y-2">
                                        {domainQueues.slice(0, 3).map(q => {
                                            const alignment = CANONICAL_INTENT_ALIGNMENTS.find(a => a.queueId === q.id);
                                            return (
                                                <li key={q.id} className="text-xs group border-b border-dashed border-gray-100 pb-1 last:border-0">
                                                    <div className="flex items-center justify-between">
                                                        <span className="font-medium text-slate-700">{q.name}</span>
                                                        <span className="text-[10px] text-slate-400 font-mono bg-slate-50 px-1 rounded">{q.id}</span>
                                                    </div>
                                                    {alignment && (
                                                        <div className="text-[10px] text-indigo-600 mt-0.5 flex items-center gap-1">
                                                            <LayoutDashboard className="w-2.5 h-2.5" />
                                                            {alignment.dashboardIntentTags[0]}
                                                        </div>
                                                    )}
                                                </li>
                                            );
                                        })}
                                        {domainQueues.length > 3 && (
                                            <li className="text-[10px] text-slate-500 pt-1 text-center italic cursor-pointer hover:text-indigo-600" onClick={() => navigate(`../queues`)}>
                                                + {domainQueues.length - 3} more in Library
                                            </li>
                                        )}
                                    </ul>
                                </div>
                            );
                        })}
                    </div>
                </CardContent>
            </Card>
        )}

      </main>

      <Dialog open={isWizardOpen} onOpenChange={setIsWizardOpen}>
          <DialogContent className="max-w-4xl h-[90vh] p-0 overflow-hidden flex flex-col">
              <DashboardWizard 
                  initialData={editingDashboard}
                  packageLevel={config.selectedPackage}
                  availableWorkTypes={workTypes}
                  onSave={handleSaveDashboard}
                  onCancel={() => setIsWizardOpen(false)}
                  userRole={currentUser.role === 'client' ? 'client' : 'consultant'}
              />
          </DialogContent>
      </Dialog>
    </div>
  );
}
